import * as R from 'ramda';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Routes } from '@/constants';
import { AnalyticsService } from '@/services';
import { selectIsB2CUser, selectSessionUserId, useAppSelector } from '@/store';

import { useLearningPathsData } from './useLearningPathsData';

export interface SidebarLeafItem {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  count?: number;
}

export interface SidebarItems {
  inProgress?: SidebarLeafItem;
  recommended?: SidebarLeafItem;
  allTopics: SidebarLeafItem & { children: SidebarLeafItem[] };
}

export const SIDEBAR_ITEMS = {
  IN_PROGRESS: 'In Progress',
  RECOMMENDED: 'Recommended',
  ALL_TOPICS: 'All Topics',
} as const;

export const useSidebarItems = (): SidebarItems => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const isB2CUser = useAppSelector(selectIsB2CUser);
  const userId = useAppSelector(selectSessionUserId);

  const { groupedByCategoryLps, recommendedLps } = useLearningPathsData();

  const selectedTopic = searchParams.get('topic');
  const isTopicsRoute = pathname === Routes.LEARNING_PATHS_TOPICS;

  const showInProgress = !!userId;
  const showRecommended = !!userId && !isB2CUser && recommendedLps.length > 0;

  const createOnClickHandler =
    (path: string, label: string, isSelected: boolean) => () => {
      const destination = isSelected ? Routes.LEARNING_PATHS : path;
      AnalyticsService.learningPathSidebarItemClicked({
        path: destination,
        label,
      });
      navigate(destination);
    };

  const isInProgressSelected = pathname === Routes.LEARNING_PATHS_IN_PROGRESS;
  const isRecommendedSelected = pathname === Routes.LEARNING_PATHS_RECOMMENDED;
  const isAllTopicsSelected = isTopicsRoute && !selectedTopic;

  const topicChildren: SidebarLeafItem[] = Object.values(
    R.mapObjIndexed((group, groupKey) => {
      const isSelected = isTopicsRoute && selectedTopic === groupKey;
      return {
        label: groupKey,
        count: group.lps.length,
        isSelected,
        onClick: createOnClickHandler(
          `${Routes.LEARNING_PATHS_TOPICS}?topic=${encodeURIComponent(groupKey)}`,
          groupKey,
          isSelected,
        ),
      };
    }, groupedByCategoryLps),
  );

  return {
    ...(showInProgress
      ? {
          inProgress: {
            label: SIDEBAR_ITEMS.IN_PROGRESS,
            isSelected: isInProgressSelected,
            onClick: createOnClickHandler(
              Routes.LEARNING_PATHS_IN_PROGRESS,
              SIDEBAR_ITEMS.IN_PROGRESS,
              isInProgressSelected,
            ),
          },
        }
      : {}),
    ...(showRecommended
      ? {
          recommended: {
            label: SIDEBAR_ITEMS.RECOMMENDED,
            isSelected: isRecommendedSelected,
            onClick: createOnClickHandler(
              Routes.LEARNING_PATHS_RECOMMENDED,
              SIDEBAR_ITEMS.RECOMMENDED,
              isRecommendedSelected,
            ),
          },
        }
      : {}),
    allTopics: {
      label: SIDEBAR_ITEMS.ALL_TOPICS,
      isSelected: isAllTopicsSelected,
      onClick: createOnClickHandler(
        Routes.LEARNING_PATHS_TOPICS,
        SIDEBAR_ITEMS.ALL_TOPICS,
        isAllTopicsSelected,
      ),
      children: topicChildren,
    },
  };
};
